import { showConfirm } from './tgDialogs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import { apiFetchJson, apiUploadFormData } from './api';
import { orderRefLabel } from './orderRef';
import {
  fileEmoji,
  formatBytes,
  canPreviewInApp,
  openExternalUrl,
  buildFilePreview,
  type FilePreviewState
} from './fileOpen';
import { FilePreviewModal } from './FilePreviewModal';

export interface OrderFilesProps {
  orderId: number;
  token: string;
  orderStatus: string;
  myRole: 'customer' | 'executor';
}

type OrderFileRow = {
  id: number;
  kind: 'sample' | 'delivery';
  folder_path?: string;
  original_name: string;
  mime_type?: string | null;
  size_bytes: number;
  created_at: string;
  purge_after?: string | null;
  uploaded_by?: number;
  uploaded_by_name: string;
  can_delete?: boolean;
};

type FolderRow = {
  id: number;
  kind: 'sample' | 'delivery';
  path: string;
  created_at: string;
  created_by?: number | null;
  can_delete?: boolean;
};

type Permissions = {
  can_upload_sample: boolean;
  can_upload_delivery: boolean;
  max_file_mb: number;
  retention_days: number;
  retention_hours?: number;
  postpone_hours?: number;
  files_purge_at?: string | null;
  can_purge_now?: boolean;
  can_postpone?: boolean;
};

type LinkModal = {
  title: string;
  url: string;
  hint: string;
};

type UploadProgress = {
  kind: 'sample' | 'delivery';
  done: number;
  total: number;
  currentName?: string;
  percent: number;
};

function uploadOverallPercent(p: UploadProgress): number {
  if (p.total <= 0) return 0;
  const filePart = Math.max(0, Math.min(100, p.percent)) / 100;
  return Math.min(100, Math.round(((p.done + filePart) / p.total) * 100));
}

function joinFolderPath(base: string, rel: string): string {
  const a = String(base || '')
    .replace(/\\/g, '/')
    .replace(/^\/+|\/+$/g, '');
  const b = String(rel || '')
    .replace(/\\/g, '/')
    .replace(/^\/+|\/+$/g, '');
  if (!a) return b;
  if (!b) return a;
  return `${a}/${b}`;
}

function fileRelativeFolder(file: File): string {
  const rel = String((file as File & { webkitRelativePath?: string }).webkitRelativePath || '');
  if (!rel) return '';
  const parts = rel.split('/').filter(Boolean);
  if (parts.length <= 1) return '';
  return parts.slice(0, -1).join('/');
}

function ProgressBar({ percent, label }: { percent: number; label?: string }) {
  const indeterminate = percent < 0;
  const width = indeterminate ? 35 : Math.max(2, Math.min(100, percent));
  return (
    <div style={{ marginBottom: 8 }}>
      {label ? (
        <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--fc-accent)', lineHeight: 1.35 }}>{label}</p>
      ) : null}
      <div
        style={{
          height: 10,
          borderRadius: 999,
          background: 'var(--fc-card-hover)',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${width}%`,
            borderRadius: 999,
            background: 'linear-gradient(90deg, var(--fc-accent), var(--fc-accent))',
            transition: indeterminate ? 'none' : 'width 0.15s ease-out',
            animation: indeterminate ? 'fc-upload-indeterminate 1.1s ease-in-out infinite' : undefined
          }}
        />
      </div>
      {!indeterminate ? (
        <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--fc-accent)', textAlign: 'right' }}>{percent}%</p>
      ) : null}
    </div>
  );
}

const HIDDEN_FILE_INPUT: CSSProperties = {
  position: 'fixed',
  left: -9999,
  top: 0,
  width: 1,
  height: 1,
  opacity: 0,
  overflow: 'hidden'
};

function FilePickButton({
  inputId,
  disabled,
  primary,
  label,
  directory,
  onFiles,
  fullWidth
}: {
  inputId: string;
  disabled?: boolean;
  primary?: boolean;
  label: string;
  directory?: boolean;
  onFiles: (files: FileList) => void;
  fullWidth?: boolean;
}) {
  return (
    <>
      <input
        id={inputId}
        type="file"
        multiple
        disabled={disabled}
        style={HIDDEN_FILE_INPUT}
        {...(directory
          ? ({ webkitdirectory: '', directory: '' } as React.InputHTMLAttributes<HTMLInputElement>)
          : {})}
        onChange={(e) => {
          const list = e.target.files;
          if (list?.length) onFiles(list);
          e.target.value = '';
        }}
      />
      <label
        htmlFor={disabled ? undefined : inputId}
        className={primary ? 'fc-btn fc-btn--primary' : 'fc-btn fc-btn--dark'}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          margin: 0,
          pointerEvents: disabled ? 'none' : 'auto',
          width: fullWidth ? '100%' : undefined,
          boxSizing: 'border-box',
          textAlign: 'center',
          display: fullWidth ? 'inline-flex' : undefined,
          alignItems: fullWidth ? 'center' : undefined,
          justifyContent: fullWidth ? 'center' : undefined,
          flex: fullWidth ? 1 : undefined,
          alignSelf: fullWidth ? 'stretch' : undefined
        }}
      >
        {label}
      </label>
    </>
  );
}

const TOGGLE_BTN: CSSProperties = {
  display: 'flex',
  width: '100%',
  maxWidth: '420px',
  boxSizing: 'border-box',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  marginTop: 16,
  padding: '12px 14px',
  background: 'rgba(var(--fc-accent-rgb), 0.12)',
  border: '2px solid rgba(var(--fc-accent-rgb), 0.35)',
  borderRadius: 10,
  color: 'var(--fc-text)',
  fontSize: 15,
  fontWeight: 700,
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit'
};

function copyToClipboard(text: string): Promise<boolean> {
  return (async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      /* fallback */
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  })();
}

function showTgAlert(message: string) {
  try {
    // @ts-ignore
    window?.Telegram?.WebApp?.showAlert?.(message);
  } catch {
    /* ignore */
  }
}

function formatPurgeUntil(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function OrderFiles({ orderId, token, orderStatus, myRole }: OrderFilesProps) {
  const [files, setFiles] = useState<OrderFileRow[]>([]);
  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [permissions, setPermissions] = useState<Permissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cloudOpen, setCloudOpen] = useState(false);
  const [desktopBusy, setDesktopBusy] = useState<'sample' | 'delivery' | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [linkModal, setLinkModal] = useState<LinkModal | null>(null);
  const [copyOk, setCopyOk] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [preview, setPreview] = useState<FilePreviewState | null>(null);
  const [cwd, setCwd] = useState<{ sample: string; delivery: string }>({ sample: '', delivery: '' });
  const [uploadMenuKind, setUploadMenuKind] = useState<'sample' | 'delivery' | null>(null);
  const [retentionBusy, setRetentionBusy] = useState<'purge' | 'postpone' | null>(null);
  const uploadBusyRef = useRef(false);

  const closeCloud = useCallback(() => {
    setCloudOpen(false);
    setLinkModal(null);
    setUploadMenuKind(null);
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!token || !orderId) return;
    if (!silent) setError(null);
    try {
      const oid = encodeURIComponent(String(orderId));
      const { response: r, data } = await apiFetchJson(`/api/orders/${oid}/files`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.status === 403 || r.status === 404) {
        setHidden(true);
        setFiles([]);
        setFolders([]);
        setPermissions(null);
        return;
      }
      if (!r.ok) {
        if (!silent) setError(String(data.error || 'Не удалось загрузить файлы'));
        return;
      }
      setHidden(false);
      setFiles(Array.isArray(data.files) ? (data.files as OrderFileRow[]) : []);
      setFolders(Array.isArray(data.folders) ? (data.folders as FolderRow[]) : []);
      setPermissions((data.permissions as Permissions) || null);
    } catch (e) {
      if (!silent) setError(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [orderId, token]);

  useEffect(() => {
    setLoading(true);
    setHidden(false);
    load();
  }, [load]);

  useEffect(() => {
    if (!cloudOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [cloudOpen]);

  useEffect(() => {
    const win = window as unknown as { __fcyxFilesCloudOpen?: boolean };
    if (cloudOpen) {
      win.__fcyxFilesCloudOpen = true;
    } else {
      delete win.__fcyxFilesCloudOpen;
    }
    window.dispatchEvent(new CustomEvent('fcyx-files-cloud-state', { detail: { open: cloudOpen } }));
    return () => {
      delete win.__fcyxFilesCloudOpen;
      window.dispatchEvent(new CustomEvent('fcyx-files-cloud-state', { detail: { open: false } }));
    };
  }, [cloudOpen]);

  useEffect(() => {
    const onCloseRequest = () => closeCloud();
    window.addEventListener('fcyx-close-files-cloud', onCloseRequest);
    return () => window.removeEventListener('fcyx-close-files-cloud', onCloseRequest);
  }, [closeCloud]);

  useEffect(() => {
    // @ts-ignore
    const tg = window?.Telegram?.WebApp;
    if (!tg?.BackButton || !cloudOpen) {
      try {
        tg?.BackButton?.hide?.();
      } catch {
        /* ignore */
      }
      return;
    }
    const onBack = () => closeCloud();
    try {
      tg.BackButton.show();
      tg.BackButton.onClick(onBack);
    } catch {
      /* ignore */
    }
    return () => {
      try {
        tg.BackButton.offClick(onBack);
        tg.BackButton.hide();
      } catch {
        /* ignore */
      }
    };
  }, [cloudOpen, closeCloud]);

  useEffect(() => {
    if (!cloudOpen) return;
    const refresh = () => {
      if (document.visibilityState === 'visible') void load(true);
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    const interval = window.setInterval(() => void load(true), 4000);
    // @ts-ignore
    const tg = window?.Telegram?.WebApp;
    try {
      tg?.onEvent?.('viewportChanged', refresh);
    } catch {
      /* ignore */
    }
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pageshow', refresh);
      window.clearInterval(interval);
      try {
        tg?.offEvent?.('viewportChanged', refresh);
      } catch {
        /* ignore */
      }
    };
  }, [cloudOpen, load]);

  async function uploadOne(
    kind: 'sample' | 'delivery',
    file: File,
    folderPath: string,
    onFileProgress?: (percent: number) => void
  ): Promise<string | null> {
    const oid = encodeURIComponent(String(orderId));
    const maxMb = permissions?.max_file_mb ?? 1024;
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > maxMb + 0.01) {
      return `«${file.name}» слишком большой (${sizeMb.toFixed(1)} МБ, макс. ${maxMb})`;
    }
    const fd = new FormData();
    fd.append('file', file);
    const q = new URLSearchParams({ kind });
    if (folderPath) q.set('folder_path', folderPath);
    const { response: r, data } = await apiUploadFormData(
      `/api/orders/${oid}/files/upload?${q.toString()}`,
      fd,
      {
        headers: { Authorization: `Bearer ${token}` },
        timeoutMs: Math.max(120000, Math.min(900000, Math.ceil(sizeMb) * 15000 + 60000)),
        onProgress: (ev) => onFileProgress?.(ev.percent)
      }
    );
    if (!r.ok) {
      if (r.status === 413) {
        return `«${file.name}» слишком большой для сервера — используйте «Ссылка для ПК»`;
      }
      return String(data.error || `Не удалось загрузить «${file.name}»`);
    }
    onFileProgress?.(100);
    return null;
  }

  async function uploadMany(
    kind: 'sample' | 'delivery',
    fileList: FileList | File[],
    baseFolder: string
  ) {
    const arr = Array.from(fileList);
    if (!arr.length || uploadBusyRef.current) return;
    setError(null);
    uploadBusyRef.current = true;
    try {
      setUploadProgress({ kind, done: 0, total: arr.length, currentName: arr[0]?.name, percent: 0 });
      const errors: string[] = [];
      for (let i = 0; i < arr.length; i++) {
        const file = arr[i];
        const folderPath = joinFolderPath(baseFolder, fileRelativeFolder(file));
        setUploadProgress({ kind, done: i, total: arr.length, currentName: file.name, percent: 0 });
        try {
          const err = await uploadOne(kind, file, folderPath, (percent) => {
            setUploadProgress({
              kind,
              done: i,
              total: arr.length,
              currentName: file.name,
              percent: percent < 0 ? 0 : percent
            });
          });
          if (err) errors.push(err);
        } catch {
          errors.push(`Ошибка сети: «${file.name}»`);
        }
      }
      await load(true);
      if (errors.length) {
        setError(errors.slice(0, 3).join('; ') + (errors.length > 3 ? '…' : ''));
        showTgAlert('Не все файлы загрузились. Проверьте список ошибок.');
      } else if (arr.length === 1) {
        showTgAlert(`Файл «${arr[0].name}» загружен`);
      } else {
        showTgAlert(`Загружено файлов: ${arr.length}`);
      }
    } finally {
      setUploadProgress(null);
      uploadBusyRef.current = false;
    }
  }

  async function createFolder(kind: 'sample' | 'delivery', baseFolder: string) {
    const name = window.prompt('Имя новой папки');
    if (!name || !String(name).trim()) return;
    setError(null);
    try {
      const oid = encodeURIComponent(String(orderId));
      const path = joinFolderPath(baseFolder, String(name).trim());
      const { response: r, data } = await apiFetchJson(`/api/orders/${oid}/files/mkdir`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ kind, path })
      });
      if (!r.ok) {
        setError(String(data.error || 'Не удалось создать папку'));
        return;
      }
      await load(true);
      showTgAlert(`Папка «${String(name).trim()}» создана`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сети');
    }
  }

  async function requestDesktopLink(kind: 'sample' | 'delivery') {
    setDesktopBusy(kind);
    setError(null);
    setCopyOk(false);
    try {
      const oid = encodeURIComponent(String(orderId));
      const { response: r, data } = await apiFetchJson(
        `/api/orders/${oid}/files/desktop-token`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ kind })
        }
      );
      if (!r.ok) {
        setError(String(data.error || 'Не удалось создать ссылку'));
        return;
      }
      if (data.page_url) {
        setLinkModal({
          title: kind === 'sample' ? 'Загрузка с ПК — образцы / ТЗ' : 'Загрузка с ПК — сдача работы',
          url: String(data.page_url),
          hint: 'Скопируйте и откройте на компьютере. Можно выбрать файлы или целую папку.'
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setDesktopBusy(null);
    }
  }

  async function downloadFile(file: OrderFileRow) {
    setDownloadingId(file.id);
    setError(null);
    try {
      const oid = encodeURIComponent(String(orderId));
      const { response: r, data } = await apiFetchJson(
        `/api/orders/${oid}/files/${file.id}/download-link`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!r.ok) {
        setError(String(data.error || 'Не удалось скачать файл'));
        return;
      }
      const url = String(data.download_url || '');
      if (!url) {
        setError('Не удалось получить ссылку на файл');
        return;
      }
      if (canPreviewInApp(file.original_name, file.mime_type)) {
        const built = await buildFilePreview(
          url,
          file.original_name,
          file.mime_type,
          file.size_bytes
        );
        if (built === 'external') {
          openExternalUrl(url);
        } else {
          setPreview(built);
        }
      } else {
        openExternalUrl(url);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сети при скачивании');
    } finally {
      setDownloadingId(null);
    }
  }

  async function deleteOwnFile(file: OrderFileRow) {
    if (!file.can_delete) return;
    if (!await showConfirm(`Удалить файл «${file.original_name}»?`)) return;
    setError(null);
    try {
      const oid = encodeURIComponent(String(orderId));
      const { response: r, data } = await apiFetchJson(
        `/api/orders/${oid}/files/${file.id}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (!r.ok) {
        setError(String(data.error || 'Не удалось удалить файл'));
        return;
      }
      await load(true);
      showTgAlert('Файл удалён');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сети');
    }
  }

  async function deleteOwnFolder(folder: FolderRow) {
    if (!folder.can_delete) return;
    const name = folder.path.includes('/')
      ? folder.path.slice(folder.path.lastIndexOf('/') + 1)
      : folder.path;
    if (!await showConfirm(`Удалить пустую папку «${name}»?`)) return;
    setError(null);
    try {
      const oid = encodeURIComponent(String(orderId));
      const { response: r, data } = await apiFetchJson(`/api/orders/${oid}/files/folder`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ kind: folder.kind, path: folder.path })
      });
      if (!r.ok) {
        setError(String(data.error || 'Не удалось удалить папку'));
        return;
      }
      await load(true);
      showTgAlert('Папка удалена');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сети');
    }
  }

  async function handleCopyLink() {
    if (!linkModal?.url) return;
    const ok = await copyToClipboard(linkModal.url);
    setCopyOk(ok);
    if (ok) showTgAlert('Ссылка скопирована');
    else setError('Не удалось скопировать — выделите ссылку вручную');
  }

  async function purgeNow() {
    if (retentionBusy) return;
    setRetentionBusy('purge');
    setError(null);
    try {
      const oid = encodeURIComponent(String(orderId));
      const { response: r, data } = await apiFetchJson(`/api/orders/${oid}/files/purge-now`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) {
        setError(String(data.error || 'Не удалось удалить файлы'));
        return;
      }
      await load(true);
      showTgAlert('Файлы удалены');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setRetentionBusy(null);
    }
  }

  async function postponePurge() {
    if (retentionBusy) return;
    setRetentionBusy('postpone');
    setError(null);
    try {
      const oid = encodeURIComponent(String(orderId));
      const { response: r, data } = await apiFetchJson(`/api/orders/${oid}/files/postpone`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) {
        setError(String(data.error || 'Не удалось отложить'));
        return;
      }
      await load(true);
      const h = data.postpone_hours ?? permissions?.postpone_hours ?? 12;
      showTgAlert(`Хранение отложено на ${h} ч`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка сети');
    } finally {
      setRetentionBusy(null);
    }
  }

  if (hidden) return null;

  const samples = files.filter((f) => f.kind === 'sample');
  const deliveries = files.filter((f) => f.kind === 'delivery');
  const canUploadSample = Boolean(permissions?.can_upload_sample);
  const canUploadDelivery = Boolean(permissions?.can_upload_delivery);
  const maxMb = permissions?.max_file_mb ?? 1024;
  const retentionHours = permissions?.retention_hours ?? (permissions?.retention_days ?? 3) * 24;
  const totalCount = files.length;
  const orderLabel = orderRefLabel(orderId);

  const showSamples =
    samples.length > 0 || canUploadSample || (myRole === 'executor' && orderStatus === 'open');
  const showDelivery =
    deliveries.length > 0 || canUploadDelivery || myRole === 'customer';

  function childFolders(kind: 'sample' | 'delivery', path: string): FolderRow[] {
    const prefix = path ? `${path}/` : '';
    return folders.filter((f) => {
      if (f.kind !== kind) return false;
      if (!prefix) {
        return f.path.indexOf('/') < 0;
      }
      if (!f.path.startsWith(prefix)) return false;
      const rest = f.path.slice(prefix.length);
      return rest.length > 0 && rest.indexOf('/') < 0;
    });
  }

  function filesInFolder(kind: 'sample' | 'delivery', path: string): OrderFileRow[] {
    return files.filter((f) => f.kind === kind && String(f.folder_path || '') === path);
  }

  /** Все файлы в этой папке и во всех вложенных */
  function filesUnderPath(kind: 'sample' | 'delivery', path: string): OrderFileRow[] {
    if (!path) return files.filter((f) => f.kind === kind);
    return files.filter((f) => {
      if (f.kind !== kind) return false;
      const fp = String(f.folder_path || '');
      return fp === path || fp.startsWith(`${path}/`);
    });
  }

  /** Все папки в этой ветке (включая текущие вложенные; для корня — все) */
  function foldersUnderPath(kind: 'sample' | 'delivery', path: string): FolderRow[] {
    return folders.filter((f) => {
      if (f.kind !== kind) return false;
      if (!path) return true;
      return f.path.startsWith(`${path}/`);
    });
  }

  function folderStats(kind: 'sample' | 'delivery', path: string) {
    const underFiles = filesUnderPath(kind, path);
    const underFolders = foldersUnderPath(kind, path);
    const bytes = underFiles.reduce((s, f) => s + (Number(f.size_bytes) || 0), 0);
    return {
      folders: underFolders.length,
      files: underFiles.length,
      bytes
    };
  }

  function formatFolderStats(kind: 'sample' | 'delivery', path: string): string {
    const s = folderStats(kind, path);
    if (s.folders === 0 && s.files === 0) return 'Папка пуста';
    return `${s.folders} пап. · ${s.files} файл. · ${formatBytes(s.bytes)}`;
  }

  function renderFolder(
    title: string,
    subtitle: string,
    kind: 'sample' | 'delivery',
    canUpload: boolean
  ) {
    const path = cwd[kind];
    const busy = uploadProgress?.kind === kind;
    const hereFiles = filesInFolder(kind, path);
    const hereFolders = childFolders(kind, path);
    const crumbs = path ? path.split('/') : [];
    const statsLabel = formatFolderStats(kind, path);
    const emptyHere = hereFolders.length === 0 && hereFiles.length === 0;

    return (
      <section
        style={{
          marginBottom: 16,
          padding: 12,
          background: 'var(--fc-card)',
          borderRadius: 14,
          border: '1px solid var(--fc-border)',
          boxShadow: 'var(--fc-shadow)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 28, lineHeight: 1 }}>📁</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 14 }}>{title}</p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.4 }}>{subtitle}</p>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 4
              }}
            >
              <p style={{ margin: 0, fontSize: 11, color: 'var(--fc-text-muted)', flex: 1, minWidth: 0 }}>
                {path ? `В этой папке: ${statsLabel}` : `Всего: ${statsLabel}`}
              </p>
              {canUpload ? (
                <button
                  type="button"
                  className="fc-btn fc-btn--dark fc-btn--sm"
                  style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                  disabled={Boolean(uploadProgress)}
                  onClick={() => void createFolder(kind, path)}
                >
                  📁 Создать
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {(crumbs.length > 0 || path) && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              alignItems: 'center',
              marginBottom: 10,
              fontSize: 12
            }}
          >
            <button
              type="button"
              className="fc-btn fc-btn--dark fc-btn--sm"
              onClick={() => setCwd((c) => ({ ...c, [kind]: '' }))}
            >
              Корень
            </button>
            {crumbs.map((seg, i) => {
              const next = crumbs.slice(0, i + 1).join('/');
              return (
                <button
                  key={next}
                  type="button"
                  className="fc-btn fc-btn--dark fc-btn--sm"
                  onClick={() => setCwd((c) => ({ ...c, [kind]: next }))}
                >
                  {seg}
                </button>
              );
            })}
          </div>
        )}

        {emptyHere ? (
          <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--fc-text-muted)' }}>Здесь пока пусто</p>
        ) : null}

        {hereFolders.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
            {hereFolders.map((f) => {
              const name = f.path.includes('/') ? f.path.slice(f.path.lastIndexOf('/') + 1) : f.path;
              const fs = folderStats(kind, f.path);
              return (
                <div key={f.id} style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                  <button
                    type="button"
                    onClick={() => setCwd((c) => ({ ...c, [kind]: f.path }))}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      flex: 1,
                      minWidth: 0,
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '1px solid rgba(var(--fc-warning-rgb), 0.35)',
                      background: 'rgba(var(--fc-warning-rgb), 0.1)',
                      color: 'var(--fc-text)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: 'inherit'
                    }}
                  >
                    <span style={{ fontSize: 22 }}>📂</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 650, fontSize: 13 }}>{name}</span>
                      <span style={{ fontSize: 11, color: 'var(--fc-text-muted)' }}>
                        {fs.folders} пап. · {fs.files} файл. · {formatBytes(fs.bytes)}
                      </span>
                    </span>
                    <span style={{ color: 'var(--fc-text-muted)' }}>›</span>
                  </button>
                  {f.can_delete ? (
                    <button
                      type="button"
                      className="fc-btn fc-btn--dark fc-btn--sm"
                      style={{ flexShrink: 0 }}
                      disabled={Boolean(uploadProgress)}
                      onClick={() => void deleteOwnFolder(f)}
                      aria-label="Удалить папку"
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {hereFiles.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: canUpload ? 10 : 0 }}>
            {hereFiles.map((f) => (
              <div key={f.id} style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                <button
                  type="button"
                  onClick={() => downloadFile(f)}
                  disabled={downloadingId === f.id || Boolean(uploadProgress)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flex: 1,
                    minWidth: 0,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '1px solid rgba(var(--fc-accent-rgb), 0.2)',
                    background: 'var(--fc-card-hover)',
                    color: 'var(--fc-text)',
                    cursor: downloadingId === f.id ? 'wait' : 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit'
                  }}
                >
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{fileEmoji(f.original_name, f.mime_type)}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {downloadingId === f.id ? 'Открываю…' : f.original_name}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--fc-text-muted)' }}>
                      {formatBytes(f.size_bytes)} · {f.uploaded_by_name}
                    </span>
                  </span>
                  <span style={{ color: 'var(--fc-accent)', fontSize: 18, flexShrink: 0 }}>
                    {canPreviewInApp(f.original_name, f.mime_type) ? '👁' : '⬇'}
                  </span>
                </button>
                {f.can_delete ? (
                  <button
                    type="button"
                    className="fc-btn fc-btn--dark fc-btn--sm"
                    style={{ flexShrink: 0 }}
                    disabled={Boolean(uploadProgress)}
                    onClick={() => void deleteOwnFile(f)}
                    aria-label="Удалить файл"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        )}

        {canUpload && (
          <>
            {busy && uploadProgress && (
              <ProgressBar
                percent={uploadProgress.percent}
                label={`Файл ${uploadProgress.done + 1}/${uploadProgress.total}${
                  uploadProgress.currentName ? `: ${uploadProgress.currentName}` : ''
                }`}
              />
            )}
            {uploadMenuKind === kind ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                  <FilePickButton
                    inputId={`fc-order-files-${orderId}-${kind}`}
                    disabled={Boolean(uploadProgress)}
                    primary={kind === 'delivery'}
                    fullWidth
                    label={busy ? 'Загрузка…' : '📄 Файлы'}
                    onFiles={(list) => {
                      setUploadMenuKind(null);
                      void uploadMany(kind, list, path);
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                  <FilePickButton
                    inputId={`fc-order-folder-${orderId}-${kind}`}
                    disabled={Boolean(uploadProgress)}
                    fullWidth
                    label="📂 Папку"
                    directory
                    onFiles={(list) => {
                      setUploadMenuKind(null);
                      void uploadMany(kind, list, path);
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="fc-btn fc-btn--dark"
                  disabled={Boolean(uploadProgress)}
                  onClick={() => setUploadMenuKind(null)}
                  style={{
                    flexShrink: 0,
                    alignSelf: 'stretch',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: 0,
                    minWidth: 44
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className={kind === 'delivery' ? 'fc-btn fc-btn--primary' : 'fc-btn fc-btn--dark'}
                  style={{ flex: 1, minWidth: 0 }}
                  disabled={Boolean(uploadProgress)}
                  onClick={() => setUploadMenuKind(kind)}
                >
                  {busy ? 'Загрузка…' : '⬆️ Загрузить'}
                </button>
                <button
                  type="button"
                  className="fc-btn fc-btn--dark"
                  style={{ flex: 1, minWidth: 0 }}
                  disabled={Boolean(uploadProgress) || desktopBusy === kind}
                  onClick={() => requestDesktopLink(kind)}
                >
                  {desktopBusy === kind ? '…' : '🔗 Ссылка для ПК'}
                </button>
              </div>
            )}
          </>
        )}
      </section>
    );
  }

  const cloudModal =
    cloudOpen && typeof document !== 'undefined'
      ? createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Файлы заказа"
            className="fc-order-files-cloud"
          >
            <div className="fc-order-files-cloud__scroll">
              <div className="fc-order-files-cloud__topbar">
                <button
                  type="button"
                  className="fc-back"
                  aria-label="Назад"
                  onClick={closeCloud}
                >
                  ←
                </button>
                <div className="fc-order-files-cloud__title-wrap">
                  <h2 className="fc-order-files-cloud__title">☁️ Облако заказа</h2>
                  <p className="fc-order-files-cloud__subtitle">{orderLabel}</p>
                </div>
                <button
                  type="button"
                  className="fc-btn fc-btn--dark fc-order-files-cloud__close"
                  onClick={closeCloud}
                >
                  ✕
                </button>
              </div>

              <p className="fc-order-files-cloud__hint">
                Можно загружать файлы и целые папки (до {maxMb} МБ на файл). Картинки, PDF, Word и Excel
                открываются внутри приложения. После завершения сделки файлы хранятся {retentionHours} ч.
              </p>

              {permissions?.files_purge_at ? (
                <div
                  style={{
                    marginBottom: 14,
                    padding: 12,
                    borderRadius: 10,
                    border: '1px solid rgba(255,180,80,0.35)',
                    background: 'rgba(var(--fc-warning-rgb), 0.12)'
                  }}
                >
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: 'var(--fc-text)' }}>
                    Скачайте файлы до {formatPurgeUntil(permissions.files_purge_at)}
                  </p>
                  <p style={{ margin: '6px 0 10px', fontSize: 12, color: 'var(--fc-text-muted)', lineHeight: 1.4 }}>
                    После этого они будут удалены. Если уже скачали — можно удалить сейчас.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {permissions.can_purge_now ? (
                      <button
                        type="button"
                        className="fc-btn fc-btn--dark"
                        disabled={Boolean(retentionBusy)}
                        onClick={() => void purgeNow()}
                      >
                        {retentionBusy === 'purge' ? '…' : '🗑 Удалить сейчас'}
                      </button>
                    ) : null}
                    {permissions.can_postpone ? (
                      <button
                        type="button"
                        className="fc-btn fc-btn--primary"
                        disabled={Boolean(retentionBusy)}
                        onClick={() => void postponePurge()}
                      >
                        {retentionBusy === 'postpone'
                          ? '…'
                          : `⏳ +${permissions.postpone_hours ?? 12} ч`}
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {error && <p className="fc-order-files-cloud__error">{error}</p>}

              {uploadProgress && (
                <div className="fc-order-files-cloud__progress">
                  <ProgressBar
                    percent={uploadOverallPercent(uploadProgress)}
                    label={`Файл ${uploadProgress.done + 1}/${uploadProgress.total}${
                      uploadProgress.currentName ? `: ${uploadProgress.currentName}` : ''
                    }`}
                  />
                </div>
              )}

              {linkModal && (
                <div className="fc-order-files-cloud__link-box">
                  <p className="fc-order-files-cloud__link-title">{linkModal.title}</p>
                  <p className="fc-order-files-cloud__link-hint">{linkModal.hint}</p>
                  <input
                    readOnly
                    className="fc-order-files-cloud__link-input"
                    value={linkModal.url}
                    onFocus={(e) => e.target.select()}
                  />
                  <div className="fc-order-files-cloud__link-actions">
                    <button type="button" className="fc-btn fc-btn--primary" onClick={() => void handleCopyLink()}>
                      {copyOk ? '✓ Скопировано' : '📋 Скопировать'}
                    </button>
                    <button
                      type="button"
                      className="fc-btn fc-btn--dark"
                      onClick={() => openExternalUrl(linkModal.url)}
                    >
                      Открыть
                    </button>
                  </div>
                </div>
              )}

              {showSamples &&
                renderFolder(
                  'Образцы / ТЗ',
                  myRole === 'customer'
                    ? 'Материалы для исполнителей: файлы, папки и загрузка целиком.'
                    : 'Материалы от заказчика — скачайте перед работой.',
                  'sample',
                  canUploadSample
                )}

              {showDelivery &&
                renderFolder(
                  'Сдача работы',
                  canUploadDelivery
                    ? 'Загрузите готовые файлы или папку — заказчик скачает отсюда.'
                    : 'Файлы от выбранного исполнителя.',
                  'delivery',
                  canUploadDelivery
                )}

              {!showSamples && !showDelivery && (
                <p className="fc-order-files-cloud__empty">Нет доступных папок для этого заказа.</p>
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <button
        type="button"
        id="freelancyx-order-files-toggle"
        style={TOGGLE_BTN}
        onClick={() => setCloudOpen(true)}
        disabled={loading}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span aria-hidden>☁️</span>
          <span>
            Файлы заказа
            {!loading && totalCount > 0 ? ` (${totalCount})` : ''}
          </span>
        </span>
        <span style={{ color: 'var(--fc-accent)', fontSize: 14, flexShrink: 0 }}>
          {loading ? '…' : 'Открыть'}
        </span>
      </button>

      {cloudModal}
      {preview ? <FilePreviewModal preview={preview} onClose={() => setPreview(null)} /> : null}
    </>
  );
}
